<template>
  <div class="c-bannerEditor">
    <BLoading
      :is-full-page="false"
      :active.sync="isLoading"
      class="c-bannerEditor_loader"
    ></BLoading>
    <ImageUploader
      @uploaded="openCropper($event)"
      :is-hovering.sync="isHovering"
      :is-uploading.sync="isLoading"
      :max-width="IMAGE_WIDTH * 2"
      ref="uploader"
    >
      <div
        class="c-bannerEditor_dropzone"
        :class="{ '-is-hovering': isHovering }"
        slot="dropzone"
      >
        <div
          class="c-bannerEditor_menu"
          v-if="!isLoading"
        >
          <BalloonDropdown position="is-bottom-center">
            <div
              class="c-bannerEditor_help"
              slot="trigger"
            >
              <CoconalaIcon
                name="camera-plus"
                color="white"
              />
              <p>
                {{ isHovering ? 'カバー画像をここにドロップしてください' : 'カバー画像を変更する' }}
              </p>
            </div>
            <BDropdownItem @click="openFinder">画像をアップロード</BDropdownItem>
            <BDropdownItem @click="doDelete">削除</BDropdownItem>
            <BDropdownItem separator></BDropdownItem>
            <BDropdownItem @click="doCancel">キャンセル</BDropdownItem>
          </BalloonDropdown>
        </div>
      </div>
    </ImageUploader>
    <no-ssr>
      <ImageCropper
        :active.sync="isCropping"
        :path="uploadedImageUri"
        :width="IMAGE_WIDTH * 2"
        :height="IMAGE_HEIGHT * 2"
        @update="attributes.coverImage = $event"
        @cancel="attributes.coverImage = coverImage"
        @cropped="onCropped($event)"
      />
    </no-ssr>
    <div class="c-bannerEditor_control">
      <CButton
        size="small"
        @click="doCancel"
        is-rounded
      >
        キャンセル
      </CButton><!--
      --><CButton
        size="small"
        color="primary"
        is-rounded
        @click="doSubmit"
      >
        変更を保存
      </CButton>
    </div>
  </div>
</template>
<script>
import BalloonDropdown from '~/components/molecules/BalloonDropdown'
import ImageCropper from '~/components/molecules/ImageCropper'
import ImageUploader from '~/components/molecules/ImageUploader'

// vuex
import { mapState, mapActions } from 'vuex'

// mixins
import formMixin from '~/mixins/form-mixin'

const IMAGE_WIDTH = 1280 // 画像の最大横幅
const IMAGE_HEIGHT = 420 // 画像の最大縦幅

export default {
  name: 'BannerEditor',
  mixins: [formMixin],
  components: {
    BalloonDropdown,
    ImageCropper,
    ImageUploader
  },
  props: {
    coverImage: {
      type: String
    },
    editingCoverImage: {
      type: String
    }
  },
  data() {
    return {
      IMAGE_WIDTH,
      IMAGE_HEIGHT,
      isHovering: false,
      isCropping: false,
      isMenuActive: true,
      isLoading: false,
      uploadedImageUri: null,
      attributes: {
        coverImage: this.coverImage
      }
    }
  },
  methods: {
    ...mapActions('pages/users/my/user', ['updateCoverImage', 'deleteCoverImage']),
    doSubmit() {
      if (this.isLoading) return
      if (this.coverImage === this.attributes.coverImage) {
        this.$emit('cancel')
        this.doNotifySuccess('カバー画像を変更しました')
        return
      }
      this.isLoading = true
      this.updateCoverImage(this.attributes)
        .then(() => {
          this.$emit('updated')
          this.doNotifySuccess('カバー画像を変更しました')
        })
        .catch(() => {
          this.$emit('update:editingCoverImage', null)
        })
        .then(() => {
          this.isLoading = false
        })
    },
    async doDelete() {
      if (await this.$util.confirm({})) {
        this.isLoading = true
        await this.deleteCoverImage()
        this.$emit('update:editingCoverImage', null)
        this.isLoading = false
        this.$emit('deleted')
        this.doNotifySuccess('カバー画像を削除しました')
      }
    },
    doCancel() {
      this.$emit('cancel')
    },
    openFinder() {
      this.$refs.uploader.openFinder()
    },
    openCropper(uri) {
      this.uploadedImageUri = uri
      this.isCropping = true
    },
    onCropped(uri) {
      this.attributes.coverImage = uri
      this.isCropping = false
    }
  },
  computed: {
    ...mapState('ui', ['viewport'])
  },
  watch: {
    'attributes.coverImage'(v) {
      this.$emit('update:editingCoverImage', v)
    }
  }
}
</script>
<style lang="scss" scoped>
.c-bannerEditor {
  user-select: none;
  &_loader {
    z-index: 4;
  }
  &_dropzone {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    background-color: rgba(0, 0, 0, 0.3);
    &:hover {
      border: 4px solid $color-link-base;
    }
    &.-is-hovering {
      border: 4px dashed $color-white;
    }
  }
  &_menu {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
    color: $color-white;
    text-align: center;
    /deep/ .dropdown-menu {
      pointer-events: auto;
    }
    cursor: pointer;
  }
  &_help {
    > p {
      margin: 0;
      font-weight: bold;
    }
    > i {
      font-size: 40px;
      font-weight: bold;
    }
  }
  &_control {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 3;
    padding: 0.5em;
    margin: 0.5em;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50px;
    .button + .button {
      margin-left: 0.5em;
    }
  }

  @media (max-width: $breakpoint-m) {
    &_dropzone {
      &:hover {
        border: none;
      }
      &.-is-hovering {
        border: 0;
      }
    }
    &_help {
      > p {
        display: none;
      }
      > i {
        font-size: 26px;
      }
    }
    &_control {
      bottom: auto;
      top: 0;
      padding: 0.25em;
      margin: 0.25em;
    }
  }
}
</style>
